import { useCurrentEntryStore } from '@/stores/currentEntry'
import { useAppUserStore } from '@/stores/appUser'
import { getDefaultPersonItem } from '@/functions/office'
import { findById } from '@/functions/expression'
import { useI18n } from 'vue-i18n'
import { TEMPLATE_RESOLVER } from "@/i18n";
import { apiDateString } from '@/api/apicommon'
import { formatUTCDateTimeToDate, formatUTCDateTimeToTime } from '@/utils/date-utils'

interface TemplateVariable {
  key: string
  description: string
}

interface TemplateVariableSection {
  section: string
  variables: TemplateVariable[]
}

interface Suggestion {
  title: string | null
  text: string
}

interface SeparatorResult {
  title: string | null
}

type TemplateResolver = typeof TEMPLATE_RESOLVER;

/**
 * Collects all variable keys across every section of the template resolver
 * into a single union type, e.g. 'patientfirstname' | 'profirstname' | 'currentdate' | ...
 */
type AllVariableKeys = {
  [S in keyof TemplateResolver]: keyof TemplateResolver[S]['variable']
}[keyof TemplateResolver];

type VariableRegistry = Record<AllVariableKeys, () => string>;

/**
 * Builds a list of available template variables grouped by section.
 * Uses the French locale file as the structural reference to discover
 * sections and variable keys, then resolves translated labels via the
 * active locale.
 */
export function getTemplateVariables(): TemplateVariableSection[] {
  const { t } = useI18n()
  return Object.entries(TEMPLATE_RESOLVER).map(([key, value]) => ({
    section: t(`patient.document.variableResolver.${key}.title`),
    variables: Object.keys(value.variable).map((v) => ({
      key: `[${v}]`,
      description: t(`patient.document.variableResolver.${key}.variable.${v}`),
    })),
  }));
}

export function resolveVariables(text: string): string {
  const currentEntryStore = useCurrentEntryStore()
  const appUserStore = useAppUserStore()

  const patient = currentEntryStore.currentPatient
  const entryHealthPro = currentEntryStore.currentHealthPro
  const appUser = appUserStore.currentHealthGroupMembership.pwr
  const hgm = appUserStore.currentHealthGroupMembership
  const iwr = hgm.instituteWithRole

  const variableRegistry: VariableRegistry = {
    // Patient
    patientfirstname:   () => patient.firstName,
    patientlastname:    () => patient.lastName,
    patientfullname:    () => `${patient.lastName} ${patient.firstName}`,
    patientbirthdate:   () => apiDateString(patient.birthDate),
    patientage:         () => `${patient.ageTerm}`,
    patientphone:       () => {
                          const phone = getDefaultPersonItem(patient?.phones)
                          return phone ? `+${phone.phonePrefix} ${phone.phoneNumber}` : ''
                        },
    patientstreet:      () => {
                          const addr = getDefaultPersonItem(patient?.addresses)
                          return addr ? addr.street : ''
                        },
    patientcity:        () => {
                          const addr = getDefaultPersonItem(patient?.addresses)
                          return addr ? addr.locality : ''
                        },
    patientfulladdress: () => {
                          const addr = getDefaultPersonItem(patient?.addresses)
                          return addr ? `${addr.street} ${addr.postalCodePrefix}${addr.postalCode} ${addr.locality}` : ''
                        },
    patientemail:       () => {
                          const email = getDefaultPersonItem(patient?.emails)
                          return email ? email.email : ''
                        },

    // Health professional (from entry)
    profirstname:       () => entryHealthPro?.firstName,
    prolastname:        () => entryHealthPro?.lastName,
    profullname:        () => `${entryHealthPro?.title} ${entryHealthPro?.lastName} ${entryHealthPro?.firstName}`,

    // Date
    currentdate:        () => formatUTCDateTimeToDate(currentEntryStore.currentEntryDate),
    currenttime:        () => formatUTCDateTimeToTime(currentEntryStore.currentEntryDate),

    // Location (from logged-in user)
    currentlocation:    () => {
                          const addr = findById(iwr.addresses, "instituteAddressId", hgm.preferredInstituteAddressId)
                          return addr ? addr.locality : ''
                        },
  }

  return text.replace(/\[(\w+)\]/g, (match: string, keyword: string): string => {
    const key = keyword.toLowerCase();
    if (key in variableRegistry) {
      return variableRegistry[key as AllVariableKeys]();  //replace e.g. [patientage] with his age
    }
    return match; //returns e.g. [130mmHg] unchanged
  })
}

const SEPARATOR_REGEX = /^\s*-{3,}(.*)$/

/**
 * Tests whether a line is a block separator (three or more dashes,
 * optionally preceded by whitespace).
 */
function parseSeparator(line: string): SeparatorResult | null {
  const match = line.match(SEPARATOR_REGEX)
  if (!match) return null
  const title = match[1].trim()
  return { title: title || null }
}

/**
 * Trims leading and trailing blank lines from an array of lines,
 * preserving blank lines in between.
 */
function trimBlankLines(lines: string[]): string[] {
  let start = 0
  while (start < lines.length && !lines[start].trim()) start++
  let end = lines.length - 1
  while (end >= start && !lines[end].trim()) end--
  return lines.slice(start, end + 1)
}

/**
 * Flushes accumulated lines into a suggestion object.
 * Returns null if the block is empty after trimming.
 */
function flushBlock(lines: string[], title: string | null): Suggestion | null {
  const trimmed = trimBlankLines(lines)
  if (trimmed.length === 0) return null
  const text = trimmed.join('\n')
  return { title, text: resolveVariables(text) }
}

/**
 * Parses a raw suggestion template string into an array of suggestion objects.
 *
 * Each object has the shape: { title: string|null, text: string }
 * - `title` is shown in the UI instead of truncated text, if present
 * - `text` is the full resolved content passed to the document on selection
 *
 * Supported syntax:
 *
 *   --- delimiter mode (when at least one --- line exists):
 *
 *     --- Certificat d'allaitement
 *     Je soussigné, Dr [profullname],
 *     certifie que la patiente [patientfullname]...
 *     ---
 *     Patient is in good health.
 *     --- Ordonnance de contrôle
 *     Contrôle dans 6 semaines.
 *
 *   - A separator is a line starting with three or more dashes (leading
 *     whitespace is stripped). Everything after the dashes (trimmed) is
 *     the block title. If empty, the block is untitled.
 *   - Content between separators forms one block. Blank lines inside
 *     a block are preserved; leading/trailing blank lines are trimmed.
 *   - Content before the first separator is one untitled block.
 *   - A trailing separator is optional — the last block closes at end of input.
 *
 *   Line-by-line mode (when no --- line exists):
 *
 *     Patient is in good health.
 *     Follow-up in 4 weeks.
 *
 *   - Every non-blank line is its own untitled suggestion.
 *
 *   Variables in [brackets] are resolved via resolveVariables().
 *
 * @param text - The raw template string to parse
 * @returns Array of suggestion objects
 */
export function splitSuggestions(text: string): Suggestion[] {
  if (!text) return []
  const lines = text.split('\n')
  const hasSeparator = lines.some(line => SEPARATOR_REGEX.test(line))

  // Line-by-line mode: no separators at all
  if (!hasSeparator) {
    return lines
      .filter(line => line.trim())
      .map<Suggestion>(line => ({ title: null, text: resolveVariables(line.trim()) }))
  }

  // Block mode
  const results: Suggestion[] = []
  let currentLines: string[] = []
  let currentTitle: string | null = null

  for (const line of lines) {
    const sep = parseSeparator(line)
    if (sep) {
      const block = flushBlock(currentLines, currentTitle)
      if (block) results.push(block)
      currentTitle = sep.title
      currentLines = []
    } else {
      currentLines.push(line)
    }
  }

  // Flush last block (final --- is optional)
  const block = flushBlock(currentLines, currentTitle)
  if (block) results.push(block)

  return results
}

/**
 * Resolves a single suggestion template string into a plain string.
 *
 * Strips --- separator lines if present, preserves original indentation
 * of content lines, and resolves [variables] via resolveVariables().
 *
 * Unlike splitSuggestions(), this always returns one single string —
 * intended for contexts where the text is used as-is rather than
 * presented as a list of selectable options.
 *
 * @param text - The raw template string to resolve
 * @returns The resolved string with variables replaced
 */
export function resolveSingleSuggestion(text: string): string | null {
  if (!text) return null
  const stripped = text
    .split('\n')
    .filter(line => !SEPARATOR_REGEX.test(line))
    .join('\n')
  return resolveVariables(stripped)
}

export function resolveSingleSuggestionWithTitle(text: string): Suggestion | null {
  return splitSuggestions(text)[0] ?? null
}